// src/components/SolutionsHub.js
import React, { useState, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useCourses } from "../hooks/useCourses";
import { FilterPanel } from "./FilterPanel";
import { ResultsTable } from "./ResultsTable";
import { Header } from "./Header";
import { StatsBar } from "./StatsBar";
import { Dashboard } from "./Dashboard";
import { HeroBanner } from "./HeroBanner";
import { PracticeOverviews } from "./PracticeOverviews";

const SORT_OPTIONS = [
  { value: "productCode",  label: "Unique ID" },
  { value: "programName",  label: "Program Name" },
  { value: "practice",     label: "Practice" },
  { value: "solutionType", label: "Solution Type" },
  { value: "expiry",       label: "Expiry" },
];

function isRecentlyUpdated(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d)) return false;
  return (new Date() - d) / 86400000 <= 30;
}

export function SolutionsHub() {
  const { accounts } = useMsal();
  const userName = accounts[0]?.name || accounts[0]?.username || "User";

  const [activeTab,             setActiveTab]            = useState("catalogue");
  const [selectedPractices,     setSelectedPractices]    = useState([]);
  const [selectedSolutionTypes, setSelectedSolutionTypes]= useState([]);
  const [selectedSalesReady,    setSelectedSalesReady]   = useState([]);
  const [searchKeyword,         setSearchKeyword]        = useState("");
  const [sortBy,                setSortBy]               = useState("productCode");
  const [sortDir,               setSortDir]              = useState("asc");
  const [priorityOnly,          setPriorityOnly]         = useState(false);
  const [newOnly,               setNewOnly]              = useState(false);
  const [updatedOnly,           setUpdatedOnly]          = useState(false);

  const queryFilters = useMemo(() => ({
    practices:     selectedPractices,
    solutionTypes: selectedSolutionTypes,
    salesReady:    selectedSalesReady,
  }), [selectedPractices, selectedSolutionTypes, selectedSalesReady]);

  const { courses, loading, error, reload } = useCourses(queryFilters);

  // Separate Practice Overview rows from individual programme rows
  const practiceOverviews = useMemo(() =>
    courses.filter((c) => c.solutionType === "Practice Overview"),
    [courses]
  );

  const programmeCourses = useMemo(() =>
    courses.filter((c) => c.solutionType !== "Practice Overview"),
    [courses]
  );

  const filtered = useMemo(() => {
    const normalise = (str) => (str || "").toLowerCase().replace(/[\s\-_]/g, "");
    const kw = normalise(searchKeyword.trim());
    const SALES_TERMS = ["sales", "salesmotion", "salesmot", "salesm", "priority"];
    const isSalesMotionSearch = kw.length > 0 && SALES_TERMS.some(t => t.startsWith(kw) || kw.startsWith(t));

    let data = isSalesMotionSearch
      ? programmeCourses.filter((c) => c.priority)
      : kw
      ? programmeCourses.filter((c) =>
          normalise(c.programName).includes(kw) ||
          normalise(c.productCode).includes(kw) ||
          normalise(c.practice).includes(kw) ||
          normalise(c.solutionType).includes(kw)
        )
      : programmeCourses;

    if (priorityOnly) data = data.filter((c) => c.priority);
    if (newOnly)      data = data.filter((c) => c.isNew);
    if (updatedOnly)  data = data.filter((c) => isRecentlyUpdated(c.modified));

    return [...data].sort((a, b) => {
      const av = (a[sortBy] || "").toString().toLowerCase();
      const bv = (b[sortBy] || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [programmeCourses, searchKeyword, sortBy, sortDir, priorityOnly, newOnly, updatedOnly]);

  const clearAllFilters = useCallback(() => {
    setSelectedPractices([]);
    setSelectedSolutionTypes([]);
    setSelectedSalesReady([]);
    setSearchKeyword("");
    setPriorityOnly(false);
    setNewOnly(false);
    setUpdatedOnly(false);
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  const toggleFilter = (setter) => (value) =>
    setter((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);

  const handleShowPriorityOnly = useCallback(() => {
    setPriorityOnly(true); setNewOnly(false); setUpdatedOnly(false);
    setActiveTab("catalogue");
  }, []);

  const handleShowNewOnly = useCallback(() => {
    setNewOnly(true); setPriorityOnly(false); setUpdatedOnly(false);
    setActiveTab("catalogue");
  }, []);

  const handleShowUpdatedOnly = useCallback(() => {
    setUpdatedOnly(true); setPriorityOnly(false); setNewOnly(false);
    setActiveTab("catalogue");
  }, []);

  const activeBanner = priorityOnly
    ? { msg: "Showing Sales Motion programs only", clear: () => setPriorityOnly(false) }
    : newOnly
    ? { msg: "Showing newly added programs only", clear: () => setNewOnly(false) }
    : updatedOnly
    ? { msg: "Showing recently updated programs (last 30 days)", clear: () => setUpdatedOnly(false) }
    : null;

  return (
    <div className="hub-layout">
      <Header userName={userName} activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="hub-main">

        {activeTab === "catalogue" && (
          <>
            <StatsBar total={filtered.length} loading={loading} />
            <HeroBanner />
            {activeBanner && (
              <div className="active-filter-banner">
                <span>{activeBanner.msg}</span>
                <button className="clear-filter-btn" onClick={activeBanner.clear}>Clear</button>
              </div>
            )}
            <FilterPanel
              selectedPractices={selectedPractices}
              onTogglePractice={toggleFilter(setSelectedPractices)}
              selectedSolutionTypes={selectedSolutionTypes}
              onToggleSolutionType={toggleFilter(setSelectedSolutionTypes)}
              selectedSalesReady={selectedSalesReady}
              onToggleSalesReady={toggleFilter(setSelectedSalesReady)}
              searchKeyword={searchKeyword}
              onSearchChange={setSearchKeyword}
              onSearch={() => {}}
              onReset={clearAllFilters}
            />
            {error && (
              <div className="error-banner">
                <span>⚠ {error}</span>
                <button onClick={reload} className="retry-btn">Retry</button>
              </div>
            )}
            <ResultsTable
              courses={filtered}
              loading={loading}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSort}
              sortOptions={SORT_OPTIONS}
              selectedSort={sortBy}
              onSortChange={setSortBy}
            />
          </>
        )}

        {activeTab === "practiceoverview" && (
          <PracticeOverviews overviews={practiceOverviews} loading={loading} />
        )}

        {activeTab === "oem" && (
          <div className="oem-placeholder">
            <div className="oem-placeholder-icon">🤝</div>
            <div className="oem-placeholder-title">OEM Section</div>
            <div className="oem-placeholder-sub">
              This section is coming soon. OEM-specific content and resources will be available here.
            </div>
            <span className="oem-placeholder-badge">Coming Soon</span>
          </div>
        )}

        {activeTab === "dashboard" && (
          <Dashboard
            courses={programmeCourses}
            loading={loading}
            error={error}
            onPriorityClick={handleShowPriorityOnly}
            onNewClick={handleShowNewOnly}
            onUpdatedClick={handleShowUpdatedOnly}
          />
        )}

      </main>
    </div>
  );
}
